import { useState } from 'react'
import {
  ArrowRightIcon as ArrowRight,
  CircleStackIcon as HardDrive,
  CloudIcon as Cloud,
  ShieldCheckIcon as ShieldCheck,
  UserPlusIcon as UserPlus,
} from '@heroicons/react/24/solid'
import { FadeUp, MicroButton } from '@/components/ui/MotionPrimitives'
import { loginCloud, registerCloud, type SessionResult } from '@/services/auth/cloud-session'

interface LoginPageProps {
  onLogin: (session: SessionResult) => Promise<void>
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const [mode, setMode] = useState<'login' | 'register'>('login')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const switchMode = (next: 'login' | 'register') => {
    setMode(next)
    setError('')
    setPassword('')
    setConfirmation('')
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError('')
    if (mode === 'register' && password !== confirmation) {
      setError('两次输入的密码不一致')
      return
    }
    setLoading(true)
    try {
      const session = mode === 'login'
        ? await loginCloud(username, password)
        : await registerCloud(username, password)
      await onLogin(session)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : '连接失败，请稍后重试')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="auth-shell">
      <FadeUp className="auth-brand">
        <img
          src="/logo.png"
          alt="闪念上岸 Logo"
          width={168}
          height={168}
        />
        <strong className="auth-brand-name">闪念上岸</strong>
        <p>登录后自动同步你的学习记录</p>
      </FadeUp>

      <FadeUp className="auth-features" delay={0.04}>
        <span className="auth-feature"><HardDrive />本地优先</span>
        <span className="auth-feature"><Cloud />自动同步</span>
        <span className="auth-feature"><ShieldCheck />密码加密</span>
      </FadeUp>

      <FadeUp className="auth-panel-wrap" delay={0.08}>
        <section className="card auth-panel">
          <div className="auth-tabs" role="tablist" aria-label="账号操作">
            <MicroButton type="button" role="tab" aria-selected={mode === 'login'} className={mode === 'login' ? 'active' : ''} onClick={() => switchMode('login')}>登录</MicroButton>
            <MicroButton type="button" role="tab" aria-selected={mode === 'register'} className={mode === 'register' ? 'active' : ''} onClick={() => switchMode('register')}>注册账号</MicroButton>
          </div>

          <p className="auth-kicker">
            {mode === 'login' ? '欢迎回来' : '创建独立学习空间'}
          </p>
          <h1 className="auth-title">
            {mode === 'login' ? '登录并同步' : '注册新账号'}
          </h1>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 14 }}>
              <label className="filter-label" htmlFor="account-username">账号</label>
              <input
                id="account-username"
                className="input"
                type="text"
                autoComplete="username"
                inputMode="text"
                placeholder="3–32 位字母、数字或下划线"
                value={username}
                onChange={event => setUsername(event.target.value)}
                minLength={3}
                maxLength={32}
                pattern="[A-Za-z0-9_]+"
                required
                autoFocus
              />
            </div>

            <div style={{ marginBottom: mode === 'register' ? 14 : 20 }}>
              <label className="filter-label" htmlFor="account-password">密码</label>
              <input
                id="account-password"
                className="input"
                type="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                placeholder="至少 8 位"
                value={password}
                onChange={event => setPassword(event.target.value)}
                minLength={8}
                maxLength={128}
                required
              />
            </div>

            {mode === 'register' && (
              <div style={{ marginBottom: 20 }}>
                <label className="filter-label" htmlFor="account-confirmation">确认密码</label>
                <input
                  id="account-confirmation"
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  placeholder="再次输入密码"
                  value={confirmation}
                  onChange={event => setConfirmation(event.target.value)}
                  minLength={8}
                  maxLength={128}
                  required
                />
              </div>
            )}

            {error && <div className="auth-error" role="alert">{error}</div>}

            <MicroButton type="submit" className="btn-primary auth-submit" disabled={loading}>
              {loading
                ? '处理中…'
                : mode === 'login'
                  ? <><ArrowRight />登录同步</>
                  : <><UserPlus />注册并进入</>}
            </MicroButton>
          </form>
          <p className="muted auth-note">
            DeepSeek 热点服务由站点统一提供，无需每个账号配置 API Key
          </p>
        </section>
      </FadeUp>
    </main>
  )
}
